import LandingLayout from './LandingLayout';
import { useState, type FormEvent } from 'react';
import { useLandingAnimations } from './useLandingAnimations';
import { useI18n } from '../../i18n/LanguageContext';
import { landingCopy } from './copy';
import { getSalesContactMessages, sendSalesInquiry } from './salesContact';
import { resolveLandingContext } from './regional';
import { useLocation } from 'react-router-dom';

export default function InvestorsPage() {
    useLandingAnimations();
    const location = useLocation();
    const { language } = useI18n();
    const { locale } = resolveLandingContext(location.pathname, language);
    const copy = landingCopy[locale].investors;
    const messages = getSalesContactMessages(locale);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formMessage, setFormMessage] = useState('');
    const handleJoinRound = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        const form = event.currentTarget;
        const formData = new FormData(form);
        const fund = String(formData.get('fund') ?? '');
        const checkSize = String(formData.get('checkSize') ?? '');
        const email = String(formData.get('email') ?? '');

        setFormMessage('');
        setIsSubmitting(true);
        try {
            await sendSalesInquiry({
                requestType: 'JOIN_ROUND',
                locale,
                fund,
                checkSize,
                email,
                sourcePage: 'investors',
            });
            setFormMessage(messages.success);
            form.reset();
        } catch {
            setFormMessage(messages.error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <LandingLayout pageKey="investors">
            <section className="hero">
                <h1 className="reveal-text">{copy.heroLine1}<br /><span>{copy.heroHighlight}</span>{copy.heroLine3}</h1>
                <p className="hero-sub reveal-text">{copy.heroSubtitle}</p>

                <div className="hero-btn-container reveal-text">
                    <a href="#contact" className="btn-hero">{copy.heroCta}</a>
                </div>

                <div className="app-showcase reveal-img">
                    <img src="/elen1-3.png" alt={copy.heroImageAlt} />
                </div>
            </section>

            <section className="features">
                <div className="container">
                    <h2>{copy.shiftTitle}</h2>
                    <div className="features-grid">
                        <div className="feature-item">
                            <div className="feature-icon"><i className="ph ph-globe"></i></div>
                            <h3>{copy.feature1Title}</h3>
                            <p>{copy.feature1Body}</p>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon"><i className="ph ph-users-three"></i></div>
                            <h3>{copy.feature2Title}</h3>
                            <p>{copy.feature2Body}</p>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon"><i className="ph ph-lock-key"></i></div>
                            <h3>{copy.feature3Title}</h3>
                            <p>{copy.feature3Body}</p>
                        </div>
                    </div>
                </div>
            </section>

            <section id="contact" className="deep-dive" style={{ background: '#fafafa', borderTop: '1px solid #eee' }}>
                <div className="container" style={{ textAlign: 'center', maxWidth: '600px', padding: '4rem 0' }}>
                    <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{copy.roundTitle}</h2>
                    <p style={{ marginBottom: '2rem' }}>{copy.roundBody}</p>

                    <form className="contact-form" onSubmit={handleJoinRound}>
                        <input type="text" name="fund" placeholder={copy.formFundPlaceholder} required />
                        <input type="text" name="checkSize" placeholder={copy.formCheckSizePlaceholder} required />
                        <input type="email" name="email" placeholder={copy.formEmailPlaceholder} required />
                        <button type="submit" disabled={isSubmitting}>{isSubmitting ? messages.sending : copy.formSubmit}</button>
                    </form>
                    {formMessage ? <p className="contact-form-feedback" role="status" aria-live="polite">{formMessage}</p> : null}
                </div>
            </section>
        </LandingLayout>
    );
}
